import type { ProseMirrorDocument, ProseMirrorMark, ProseMirrorNode } from "./serialize";

/**
 * Editor-document normalization — the hygiene pass every path that fills the
 * editor runs through (draft load, Word import via `htmlToDocument`).
 *
 * Three residues reach a document from outside the editor:
 *
 *   - **Markdown escape residue.** The serializer escapes `` \ ` * _ [ ] < >
 *     # `` (and `|` inside table cells) on the way out, so published text
 *     round-trips. Source documents made from exported Markdown carry those
 *     sequences already — stacked several layers deep when a document has
 *     passed through more than one export — and each older save re-escaped
 *     the backslashes, so the pile only grew. The editor would show a `\` the
 *     author never wrote. Text is dissolved to a fixed point here; code spans
 *     and code blocks are literal and are left alone, matching how the
 *     serializer escapes.
 *   - **Underlined headings.** Word documents routinely style subheadings
 *     with underline, which mammoth carries over as `<u>`. A heading that
 *     publishes underlined is import noise, not intent, and the serializer
 *     refuses to write it (serialize.ts); normalization strips the mark on
 *     the way in so both sides of the round trip agree: headings are never
 *     underlined.
 *   - **Self-anchoring headings.** Markdown exported from other tools wraps
 *     heading text in a link to its own anchor:
 *     `[标题](#%E6%A0%87%E9%A2%98)`. The site regenerates heading anchors
 *     (`rehype-slug` + autolink headings), so the link carries nothing — but
 *     in the editor it renders the heading as a link, underlined like any
 *     `.prose a`, and in the committed file it is noise. Fragment-only links
 *     are stripped from headings; links to other pages stay.
 *
 * Pure document surgery — no DOM, no editor instance — so it runs anywhere
 * the editor package does and tests without a browser.
 */

/**
 * One level of backslash escapes, mirroring what the serializer can emit
 * (`escapeInlineText`'s set, plus the table-cell pipe).
 */
const RESIDUE = /\\([\\`*_[\]<>#|])/g;

/** An upper bound that no real document out-stacks; the loop stops earlier. */
const RESIDUE_MAX_PASSES = 8;

/**
 * Dissolve Markdown escape residue in plain text — every level of it, not one
 * per call: a source that has been exported more than once arrives with
 * `\\*`-style stacks, and a single pass would leave a backslash behind.
 */
export function unescapeResidue(text: string): string {
  let out = text;

  for (let pass = 0; pass < RESIDUE_MAX_PASSES; pass += 1) {
    const next = out.replace(RESIDUE, "$1");

    if (next === out) {
      break;
    }

    out = next;
  }

  return out;
}

/**
 * Return a copy of `doc` with escape residue dissolved and heading underline
 * marks removed. Text that is already clean is returned unchanged (by
 * identity), so running it twice is a no-op.
 */
export function normalizeDocument(doc: ProseMirrorDocument): ProseMirrorDocument {
  const content = (doc.content ?? []).map((node) => normalizeNode(node, false, false));

  return content === doc.content ? doc : { ...doc, content };
}

function normalizeNode(node: ProseMirrorNode, inHeading: boolean, inCode: boolean): ProseMirrorNode {
  if (node.type === "text") {
    // Code spans are literal, exactly as in the serializer's escape step.
    const inCodeSpan = node.marks?.some((mark) => mark.type === "code") === true;
    const text =
      inCode || inCodeSpan || node.text === undefined ? node.text : unescapeResidue(node.text);
    const marks = inHeading ? headingMarks(node.marks) : node.marks;

    if (text === node.text && marks === node.marks) {
      return node;
    }

    return { ...node, text, marks };
  }

  const heading = inHeading || node.type === "heading";
  const code = inCode || node.type === "codeBlock";
  const content = node.content?.map((child) => normalizeNode(child, heading, code));

  return content === node.content ? node : { ...node, content };
}

/**
 * The marks a heading may keep. Underline never; a fragment link never (a
 * heading that links to its own anchor is export residue — the site
 * regenerates heading anchors). Anything else, including a link to another
 * page, stays.
 */
function headingMarks(marks: readonly ProseMirrorMark[] | undefined) {
  if (marks === undefined || !marks.some(isHeadingResidue)) {
    return marks;
  }

  return marks.filter((mark) => !isHeadingResidue(mark));
}

function isHeadingResidue(mark: ProseMirrorMark): boolean {
  if (mark.type === "underline") {
    return true;
  }

  if (mark.type === "link") {
    return typeof mark.attrs?.href === "string" && mark.attrs.href.startsWith("#");
  }

  return false;
}
